// ===================================================================
// src/app/hooks/useChatNotifications.ts
//
// Store compartilhado (fora do React, padrão useSyncExternalStore) de
// não-lidas + prévia da última mensagem por grupo de chat, alimentado
// pelo canal WS global (src/app/lib/chatNotifySocket.ts). A conexão em
// si só é aberta uma vez, por AppLayout.tsx (initChatNotifications) —
// este hook nunca conecta nada, só lê o snapshot compartilhado, então é
// seguro chamar de vários componentes (ChatGroupList, futuros badges de
// sidebar, etc.) sem abrir conexões/duplicar toasts.
// ===================================================================

import { useSyncExternalStore } from 'react';
import { chatNotifySocket, type ChatNotifyLastMessage, type ChatNotifyPayload } from '../lib/chatNotifySocket';
import { chatService } from '../services/chatService';

export interface ChatNotifyState {
  unreadByGroup: Record<string, number>;
  lastMessageByGroup: Record<string, ChatNotifyLastMessage>;
}

let state: ChatNotifyState = { unreadByGroup: {}, lastMessageByGroup: {} };
const listeners = new Set<() => void>();
let initialized = false;

function setState(next: ChatNotifyState) {
  state = next;
  listeners.forEach((l) => l());
}

function applyUpdate(payload: ChatNotifyPayload) {
  setState({
    unreadByGroup: { ...state.unreadByGroup, [payload.group_id]: payload.unread_count },
    lastMessageByGroup: { ...state.lastMessageByGroup, [payload.group_id]: payload.last_message },
  });
}

/** Fallback de resync — chamado no boot e sempre que o socket (re)conecta,
 * pra corrigir o estado caso algum frame tenha sido perdido offline. */
function resync() {
  chatService
    .getUnreadCount()
    .then((res) => setState({ ...state, unreadByGroup: res.by_group }))
    .catch(() => {
      // silencioso — o badge só fica desatualizado até o próximo resync
    });
}

/** Conecta o socket global de notificações e inicia o resync. Idempotente —
 * chamar mais de uma vez (ex: HMR, StrictMode) não abre conexões extras.
 * Deve ser chamado uma única vez, em AppLayout.tsx. */
export function initChatNotifications() {
  if (initialized) return () => {};
  initialized = true;

  chatNotifySocket.on('open', resync);
  chatNotifySocket.on('chat.notify.update', applyUpdate);
  chatNotifySocket.connect();
  resync();

  return () => {
    chatNotifySocket.off('open', resync);
    chatNotifySocket.off('chat.notify.update', applyUpdate);
    chatNotifySocket.disconnect();
    initialized = false;
  };
}

/** Zera otimisticamente o badge de um grupo (ex: ao abri-lo) — o próximo
 * evento real ou resync corrige o valor se este palpite estiver errado. */
export function markGroupReadLocally(groupId: string) {
  if (!state.unreadByGroup[groupId]) return;
  setState({ ...state, unreadByGroup: { ...state.unreadByGroup, [groupId]: 0 } });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

/** Não-lidas e prévia da última mensagem por grupo, atualizadas ao vivo.
 * Só leitura — a conexão é gerenciada por initChatNotifications() em
 * AppLayout.tsx, não por este hook. */
export function useChatNotifications(): ChatNotifyState {
  return useSyncExternalStore(subscribe, getSnapshot);
}
